import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { ErrorMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import LoadingSpinner from "../../../_UI/Components/LoadingSpinner";
import { PathwayButton } from "../../../_UI/Components/PathwayButtons";
import PathwayStatusPill from "../../../_UI/Components/PathwayStatusPill";
import {
    PathwayTable,
    PathwayTableCellContent,
    PathwayTableContentRow,
    PathwayTableHeader,
    PathwayTableHeaderRow,
    TableContentDividerLine,
} from "../../../_UI/StyledComponents/PathwayTable";
import { isShopifyBillingAppType, isShopifyExportAppType } from "../../../_statics/AuthDataType";
import { getIntegrationSquareLogo } from "../../../_statics/IntegrationBrand";
import { getShopifyWebhookHealthForOrganization } from "../services/clients.actions";
import { isShopifyAppstoreConnection } from "./connectionStatus";
import ShopifyWebhookHealthModal from "./ShopifyWebhookHealthModal";
import { formatCheckedAt, webhookHealthPill } from "./shopifyWebhookHealth";
import { Logo, Mono, Muted } from "./shopifyWebhookHealth.styles";

const appLabel = (authDataType) => {
    if (isShopifyExportAppType(authDataType)) {
        return "Buchhaltungsexport";
    }
    if (isShopifyBillingAppType(authDataType)) {
        return "Rechnungsprinter";
    }
    return null;
};

// When the health endpoint is down the tab still has to list the shops, so the org's Shopify connections are
// shown as never-checked rows built from what the client reducer already holds.
const uncheckedRowFromConnection = (connection) => ({
    authDataUUID: connection.externalUUID,
    shopDomain: connection.endpoint,
    authDataType: connection.type,
    checkedAt: null,
    platformAudit: null,
    handlerAudit: null,
});

const ShopifyWebhookHealthTabContent = () => {
    const dispatch = useDispatch();
    const usedConnectedAuthData = useSelector((state) => state.client.usedConnectedAuthData);
    const healthRows = useSelector((state) => state.client.shopifyWebhookHealth.rows);
    const loaded = useSelector((state) => state.client.shopifyWebhookHealth.loaded);
    const loadError = useSelector((state) => state.client.shopifyWebhookHealth.errorMessage);
    const byUUID = useSelector((state) => state.client.shopifyWebhookHealth.byUUID);

    const [openConnection, setOpenConnection] = useState(null);

    useEffect(() => {
        dispatch(getShopifyWebhookHealthForOrganization());
    }, [dispatch]);

    // The per-credential entries are laid over the loaded list, so a check run from the modal shows up on its
    // row without the list having to be fetched again.
    const rows = useMemo(() => {
        const shopifyConnections = (usedConnectedAuthData || []).filter(isShopifyAppstoreConnection);
        const source =
            loadError && healthRows.length === 0 ? shopifyConnections.map(uncheckedRowFromConnection) : healthRows;
        return source.map((health) => {
            const connection = shopifyConnections.find((candidate) => candidate.externalUUID === health.authDataUUID);
            const entry = byUUID[health.authDataUUID];
            const merged = entry?.dto ? { ...health, ...entry.dto } : health;
            return {
                ...merged,
                title: connection?.title || merged.shopDomain,
            };
        });
    }, [healthRows, loadError, usedConnectedAuthData, byUUID]);

    const lastCheckLabel = useMemo(() => {
        if (rows.length === 0) {
            return "Nicht geprüft";
        }
        const latest = rows.reduce((max, row) => {
            if (!row.checkedAt) {
                return max;
            }
            return !max || new Date(row.checkedAt) > new Date(max) ? row.checkedAt : max;
        }, null);
        return formatCheckedAt({ checkedAt: latest });
    }, [rows]);

    const handleClose = useCallback(() => setOpenConnection(null), []);

    return (
        <>
            <Intro>
                <IntroText>
                    Für jede Shopify-Verbindung kannst du prüfen, ob alle benötigten Webhooks bei Shopify registriert
                    sind. Fehlende Webhooks bedeuten, dass Bestellungen nicht automatisch importiert werden.
                </IntroText>
                <LastCheck data-cy="shopify-webhook-health-last-check">Letzte Prüfung: {lastCheckLabel}</LastCheck>
            </Intro>

            {loadError && <ErrorMessage>{loadError}</ErrorMessage>}

            {!loaded ? (
                <LoadingSpinner loading={true} />
            ) : (
                <PathwayTable data-cy="shopify-webhook-health-table">
                    <TableHeader>
                        <PathwayTableHeader>Verbindung</PathwayTableHeader>
                        <PathwayTableHeader>Shop-Domain</PathwayTableHeader>
                        <PathwayTableHeader>Webhooks</PathwayTableHeader>
                        <PathwayTableHeader>Letzte Prüfung</PathwayTableHeader>
                        <PathwayTableHeader />
                    </TableHeader>
                    {rows.length === 0 && <EmptyRow>Diese Organisation hat keine Shopify-Verbindungen.</EmptyRow>}
                    {rows.map((row) => {
                        const pill = webhookHealthPill(row);
                        const label = appLabel(row.authDataType);
                        return (
                            <React.Fragment key={row.authDataUUID}>
                                <TableRow data-cy={"shopify-webhook-health-row-" + row.authDataUUID}>
                                    <PathwayTableCellContent>
                                        <ConnectionCell>
                                            <Logo src={getIntegrationSquareLogo(row.authDataType)} alt="" />
                                            <ConnectionText>
                                                <ConnectionTitle title={row.title}>{row.title}</ConnectionTitle>
                                                {label && <ConnectionMeta>{label}</ConnectionMeta>}
                                            </ConnectionText>
                                        </ConnectionCell>
                                    </PathwayTableCellContent>
                                    <PathwayTableCellContent>
                                        <Mono title={row.shopDomain}>{row.shopDomain}</Mono>
                                    </PathwayTableCellContent>
                                    <PathwayTableCellContent>
                                        {pill ? (
                                            <PathwayStatusPill
                                                severity={pill.severity}
                                                dataCy={"shopify-webhook-health-pill-" + row.authDataUUID}
                                            >
                                                {pill.label}
                                            </PathwayStatusPill>
                                        ) : (
                                            <Muted>Nicht geprüft</Muted>
                                        )}
                                    </PathwayTableCellContent>
                                    <PathwayTableCellContent>
                                        <Muted>{formatCheckedAt(row)}</Muted>
                                    </PathwayTableCellContent>
                                    <ActionCell>
                                        <PathwayButton
                                            color="secondary"
                                            size="sm"
                                            onClick={() => setOpenConnection(row)}
                                            dataCy={"shopify-webhook-health-check-button-" + row.authDataUUID}
                                        >
                                            Webhook-Status prüfen
                                        </PathwayButton>
                                    </ActionCell>
                                </TableRow>
                                <TableContentDividerLine />
                            </React.Fragment>
                        );
                    })}
                </PathwayTable>
            )}

            <ShopifyWebhookHealthModal
                connection={openConnection}
                isOpen={openConnection !== null}
                onClose={handleClose}
            />
        </>
    );
};

export default ShopifyWebhookHealthTabContent;

const Intro = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--gap-l);
    margin-bottom: var(--gap-l);
`;

const IntroText = styled.p`
    max-width: 720px;
    margin: 0;
    color: var(--text-muted);
    line-height: var(--pw-leading-normal);
`;

const LastCheck = styled.span`
    flex: none;
    font-size: var(--pw-text-sm);
    color: var(--text-subtle);
`;

const COLUMNS = "minmax(0, 1.4fr) minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 0.9fr) max-content";

const TableHeader = styled(PathwayTableHeaderRow)`
    grid-template-columns: ${COLUMNS};
`;

const TableRow = styled(PathwayTableContentRow)`
    grid-template-columns: ${COLUMNS};
    align-items: center;
`;

const EmptyRow = styled.div`
    padding: 16px 20px;
    color: var(--text-muted);
`;

const ConnectionCell = styled.div`
    display: flex;
    align-items: center;
    gap: var(--gap-s);
    min-width: 0;
`;

const ConnectionText = styled.div`
    display: grid;
    min-width: 0;
`;

const ConnectionTitle = styled.span`
    font-weight: var(--pw-weight-bold);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ConnectionMeta = styled.span`
    font-size: var(--pw-text-sm);
    color: var(--text-subtle);
`;

const ActionCell = styled(PathwayTableCellContent)`
    justify-items: end;
`;
